import {JOBFEED_SLICE_NAME} from '@jobseeker/common-core/constants';
import {createAction, createSlice} from '@reduxjs/toolkit';
import {AppState} from '@jobseeker/common-core/types';
import {HYDRATE} from 'next-redux-wrapper';
import {enqueueSnackbar} from 'notistack';
import {
  alumniPageJobs,
  fetchCategoryJobFeed,
  fetchIndustryData,
  fetchLocationBasedJobFeed,
  fetchRecruiterJobs,
  fetchRecuiterJobFeed,
  fetchSimilarJobs,
  fetchTagJobFeed,
  fetchUserJobFeed,
  findAppliedJobs,
  findCompanySimilarJobs,
  findJobsForHer,
  findSavedJobs,
  saveJob,
  searchJobsbyQuery,
  unFollowJob,
  unFollowRecruiter,
} from './thunkServices/jobfeed';

import type {
  AppliedJobsInterface,
  Job,
  JobFeedState,
} from '@jobseeker/common-core/types';

const initialState: Partial<JobFeedState> = {
  jobfeed: [],
  filters: {sort: []},
  filterChips: [],
  loadMoreEnabled: null,
  fetchedPageNo: 0,
  isLoading: true,
  industryOptions: [],
  totalJobs: 0,
  isFilterApplied: false,
};

const HyDrate = createAction<AppState>(HYDRATE);

const jobFeedCommonReducer = (state: any, action: any) => {
  let jobfeed: Job[] | AppliedJobsInterface[] =
    action.payload?.jobs ||
    action.payload?.job?.jobfeed ||
    action.payload?.data ||
    [];

  // if (state.fetchedPageNo != action?.meta?.arg?.pageNo) {
  //   jobfeed = state.jobfeed?.concat(jobfeed);
  // }

  if (action?.meta?.arg?.concat) {
    jobfeed = state.jobfeed?.concat(jobfeed);
  }

  return {
    ...state,
    jobfeed,
    loadMoreEnabled: action.payload?.hasMore,
    fetchedPageNo: action?.meta?.arg?.pageNo || 0,
    isLoading: false,
    loadingSource: null,
    isFilterApplied: false,
    totalJobs: action?.payload?.totalJobs || 0,
  };
};

const jobFeedSlice = createSlice({
  name: JOBFEED_SLICE_NAME,
  initialState,
  reducers: {
    set_initial_state: (state, action) => {
      return {
        ...initialState,
        industryOptions: state.industryOptions,
      };
    },
    set_jobfeed: (state, action) => {
      return jobFeedCommonReducer(state, action);
    },
    set_filters: (state, action) => {
      const {filters, options} = action.payload;

      if (options.showSort) {
        if (!filters.sort || !filters.sort?.length) {
          if (state.filters?.sort?.includes('date') && !options.clearFilter)
            filters.sort = ['relevance'];
          else {
            filters.sort = ['date'];
          }
        }
      }

      return {
        ...state,
        filters: filters,
      };
    },
    set_filter_chips: (state, action) => {
      const {chips} = action.payload;
      return {
        ...state,
        filterChips: chips,
      };
    },
    remove_filter_chip: (state, action) => {
      const {chipLabel} = action.payload;
      return {
        ...state,
        filterChips: state.filterChips?.filter(c => c.label !== chipLabel),
      };
    },
    set_jobfeed_loading: (state, action) => {
      return {
        ...state,
        isLoading: action.payload.isLoading,
        loadingSource: action.payload.loadingSource,
      };
    },
    set_jobfeed_filter_loaded: (state, action) => {
      return {
        ...state,
        isFilterLoaded: action.payload,
      };
    },
    set_filter_applied: (state, action) => {
      return {
        ...state,
        isFilterApplied: action.payload.isFilterApplied,
      };
    },
    set_similarjobs: (state, action) => {
      return jobFeedCommonReducer(state, action);
    },
    hide_job: (state, action) => {
      const jobId = action.payload;
      state.jobfeed = state.jobfeed?.filter(j => j.id != jobId);
    },
    set_loadMore: (state, action) => {
      return {
        ...state,
        loadMoreEnabled: action.payload,
      };
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchUserJobFeed.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });
    builder.addCase(fetchUserJobFeed.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(saveJob.fulfilled, (state, action) => {
      if (state.jobfeed && state.jobfeed?.length > -1) {
        const actionableJob = state.jobfeed.find(
          job => job.id === action.meta.arg.jobId
        );
        enqueueSnackbar(action.payload.message, {variant: 'success'});
        if (actionableJob) {
          return {
            ...state,
            jobfeed: state.jobfeed.map(job => {
              if (job.id === action.meta.arg.jobId) {
                return {...job, saved: action.meta.arg.unsave ? 0 : 1};
              }
              return job;
            }),
          };
        }
      }
    });
    builder.addCase(saveJob.rejected, (state: any, action: any) => {
      enqueueSnackbar(action.payload?.error?.message, {variant: 'error'});

      // console.error(action.payload?.error?.message, 'rejected.payload');
    });

    builder.addCase(unFollowJob.fulfilled, (state, action) => {
      // alert(action.payload.message);
      enqueueSnackbar(action.payload.message, {variant: 'success'});
    });
    builder.addCase(unFollowJob.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(unFollowRecruiter.fulfilled, (state, action) => {
      // alert(action.payload.message);
      enqueueSnackbar(action.payload.message, {variant: 'success'});
    });
    builder.addCase(unFollowRecruiter.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(fetchCategoryJobFeed.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });
    builder.addCase(fetchCategoryJobFeed.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(fetchRecuiterJobFeed.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });
    builder.addCase(fetchRecuiterJobFeed.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(fetchTagJobFeed.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });
    builder.addCase(fetchTagJobFeed.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(fetchLocationBasedJobFeed.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });
    builder.addCase(fetchLocationBasedJobFeed.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(fetchSimilarJobs.fulfilled, (state, action) => {
      return {...jobFeedCommonReducer(state, action), loadMoreEnabled: false};
    });
    builder.addCase(fetchSimilarJobs.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(findSavedJobs.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });

    builder.addCase(findSavedJobs.pending, (state, action) => {
      return console.log('Loading saved jobs');
    });

    builder.addCase(findSavedJobs.rejected, (state, action) => {
      return console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(findAppliedJobs.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });

    builder.addCase(findAppliedJobs.pending, (state, action) => {
      return console.log('Loading Applied jobs');
    });

    builder.addCase(findAppliedJobs.rejected, (state, action) => {
      return console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(findCompanySimilarJobs.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });

    builder.addCase(findCompanySimilarJobs.pending, (state, action) => {
      return console.log('Loading company similar jobs');
    });

    builder.addCase(findCompanySimilarJobs.rejected, (state, action) => {
      return console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(fetchRecruiterJobs.fulfilled, (state, action) => {
      return {
        ...state,
        jobfeed: action.payload.jobs || [],
        loadMoreEnabled: action.payload?.hasMore ?? false,
        totalJobs: action.payload?.totalJobs || 0,
      };
    });
    builder.addCase(fetchRecruiterJobs.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(fetchIndustryData.fulfilled, (state, action) => {
      return {
        ...state,
        industryOptions: action.payload || [],
      };
    });
    builder.addCase(fetchIndustryData.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(findJobsForHer.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });

    builder.addCase(findJobsForHer.pending, (state, action) => {
      console.log('Loading wow jobs');
      return {
        ...state,
        isLoading: true,
      };
    });

    builder.addCase(findJobsForHer.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
      return {
        ...state,
        isLoading: false,
      };
    });

    builder.addCase(searchJobsbyQuery.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });
    builder.addCase(searchJobsbyQuery.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
      console.log(state, 'rejected.payload');
      return {...state, loadMoreEnabled: false, isFilterLoaded: true};
    });
    builder.addCase(alumniPageJobs.fulfilled, (state, action) => {
      return jobFeedCommonReducer(state, action);
    });
    builder.addCase(alumniPageJobs.pending, state => {
      return {
        ...state,
        isLoading: true,
      };
    });
    builder.addCase(alumniPageJobs.rejected, (state, action) => {
      console.error(action.payload, 'alumniPageJobs rejected');
      return {
        ...state,
        isLoading: false,
        loadMoreEnabled: false,
      };
    });
    builder.addCase(HyDrate, (state, action) => {
      if (!(action.payload?.job?.jobfeed?.length > 0)) {
        return state;
      }

      state.jobfeed = action.payload?.job?.jobfeed || [];
      state.loadMoreEnabled = action.payload?.job?.loadMoreEnabled || false;
      state.fetchedPageNo = action.payload?.job?.fetchedPageNo || 0;
    });
  },
});

const reducer = jobFeedSlice.reducer;

export {initialState, reducer};
